import { btnSecondaryCss } from "@components/Button/utils/constants";
import { TestIdClassNameProps } from "@typings/Common";
import { ArticleDataObject, Tag } from "@typings/Cue";
import { cn, isArticleUpdated } from "@util/helpers";

import BasicCard from "../ArticleCard/BasicCard";
import { CardVariations } from "../ArticleCard/types";
import KeywordFilter from "../KeywordFilter";

export type MyBTCarouselSlideProps = TestIdClassNameProps & {
  articles: ArticleDataObject[];
  tag: Tag;
  disablePaywall?: boolean;
  /** The class name for the wrapper of the slide */
  className?: string;
  /** The class name for the divider */
  dividerClassName?: string;
  /** The class name for the icon */
  iconClassName?: string;
};

export default function MyBTCarouselSlide({
  testId,
  className,
  articles,
  tag,
  dividerClassName,
  iconClassName,
}: MyBTCarouselSlideProps): React.ReactElement {
  return (
    <div
      className={cn("h-full border border-gray-850 p-4", className)}
      data-testid={testId}
    >
      {tag.urlPath ? (
        <KeywordFilter
          tag={tag}
          tagClassName="px-0"
          iconClassName={iconClassName}
          buttonClassName={cn(btnSecondaryCss, "p-2")}
          disableTooltip
          disableBorder
          testId={testId ? `${testId}-mybt-keyword-button` : undefined}
        />
      ) : null}

      <div
        className={cn(
          "stories divide mt-3 divide-y divide-gray-175 border-t border-gray-175",
          dividerClassName
        )}
      >
        {articles?.map((article, index) => {
          const {
            id,
            title,
            urlPath,
            media,
            edited,
            updated,
            sections,
            displaySetting,
          } = article;
          const mainSection = sections?.[0];

          const variant: CardVariations = {
            ...(index === 0
              ? {
                  image: {
                    width: "w-3/10",
                    position: "right",
                    aspectRatio: "aspect-square object-cover",
                    filters: [
                      {
                        breakpoint: "(min-width: 1200px)",
                        w: 150,
                        h: 100,
                        dpr: 1,
                        f: "webp",
                      },
                      {
                        breakpoint: "(min-width: 1024px)",
                        w: 120,
                        h: 80,
                        dpr: 1,
                        f: "webp",
                      },
                      {
                        breakpoint: "(min-width: 992px)",
                        w: 204,
                        h: 136,
                        dpr: 1,
                        f: "webp",
                      },
                      {
                        breakpoint: "(min-width: 768px)",
                        w: 144,
                        h: 96,
                        dpr: 1,
                        f: "webp",
                      },
                      { w: 132, h: 88, dpr: 1, f: "webp" },
                    ],
                  },
                  content: {
                    width: "w-7/10",
                    extraClass: "mr-4",
                  },
                }
              : null),
            title: {
              color: "text-gray-850",
              size: "text-base",
              extraClass: "my-0",
            },
          };

          return (
            <BasicCard
              key={`${id}_${index}`}
              id={id}
              title={displaySetting?.displayHeadline || title}
              media={index === 0 ? media : undefined}
              slug={urlPath}
              variations={variant}
              rootClassName={cn("story flex-nowrap py-3")}
              edited={isArticleUpdated(updated, edited) ? edited : undefined}
              hasUpdatedTimestampDisplay={
                displaySetting?.hasUpdatedTimeDisplayed
              }
              defaultImage={
                index === 0 && mainSection
                  ? { directoryName: mainSection?.uniqueName }
                  : undefined
              }
            />
          );
        })}
      </div>
    </div>
  );
}
